/**
 * Phase 27.31 — Shared constants for Lead Landing Pages.
 *
 * The service catalog is kept as a single source of truth so the
 * admin editor, the public renderer, and the (future) statistics
 * views all show the same labels and ordering. The keys must match
 * `LANDING_SERVICE_KEYS` on the backend.
 */

export const SERVICE_CATALOG = [
  { key: 'medicare', label: 'Medicare' },
  { key: 'individual_family_health', label: 'Individual & Family Health Insurance' },
  { key: 'life', label: 'Life Insurance' },
  { key: 'dental_vision', label: 'Dental & Vision' },
  { key: 'supplemental', label: 'Supplemental Insurance' },
  { key: 'cancer_critical_illness', label: 'Cancer & Critical Illness Insurance' },
  { key: 'annuities_retirement', label: 'Annuities / Retirement' },
  { key: 'travel', label: 'Travel Insurance' },
  { key: 'other', label: 'Other Insurance Needs' },
];

export const TEMPLATE_STYLES = [
  {
    key: 'professional',
    label: 'Professional',
    description: 'Clean, corporate, neutral. Works for all products.',
  },
  {
    key: 'medicare',
    label: 'Medicare',
    description: 'Larger typography, stronger readability. Designed for 65+ audience.',
  },
  {
    key: 'family_health',
    label: 'Family Health',
    description: 'Warmer, family-oriented tone.',
  },
  {
    key: 'modern_minimal',
    label: 'Modern Minimal',
    description: 'Editorial black with rose accents. Sleek and contemporary — great for younger prospects.',
  },
  {
    key: 'elegant_dark',
    label: 'Elegant Dark (Luxury)',
    description: 'Charcoal with champagne gold. Designed for annuities, retirement, and high-net-worth prospects.',
  },
  {
    key: 'warm_latino',
    label: 'Warm Latino',
    description: 'Terracotta and amber gradient. Warm, trustworthy tone for Hispanic-community outreach.',
  },
  {
    key: 'wellness_bright',
    label: 'Wellness Bright',
    description: 'Rose-to-orange with sunny yellow. Cheerful vibe for dental, vision, and wellness products.',
  },
  {
    key: 'ocean_blue',
    label: 'Ocean Blue',
    description: 'Cyan-to-blue gradient with aqua accents. Fresh and trustworthy.',
  },
  {
    key: 'royal_purple',
    label: 'Royal Purple',
    description: 'Deep violet with lavender accents. Premium and distinctive.',
  },
  {
    key: 'crimson_red',
    label: 'Crimson Red',
    description: 'Bold red-to-rose gradient. Confident and high-energy.',
  },
  {
    key: 'forest_green',
    label: 'Forest Green',
    description: 'Rich emerald gradient. Natural, calm and reassuring.',
  },
  {
    key: 'sunset_coral',
    label: 'Sunset Coral',
    description: 'Rose-to-amber warmth. Friendly and inviting.',
  },
  {
    key: 'midnight_gold',
    label: 'Midnight Gold',
    description: 'Navy charcoal with champagne gold. Elegant and premium.',
  },
];

/**
 * Landing page MODELS (functional layouts). Each maps to a `layout`
 * key stored on the page. The public renderer draws the matching
 * experience; the step-by-step editor shows only the steps that
 * apply to the chosen model.
 */
export const LANDING_MODELS = [
  { key: 'standard', label: 'Full marketing page', icon: 'LayoutTemplate',
    desc: 'Complete multi-service page: hero, services, trust and a lead form.' },
  { key: 'lead_capture', label: 'Lead capture / Quote', icon: 'ClipboardList',
    desc: 'Form-first page to collect quote requests fast.' },
  { key: 'single_product', label: 'Single product focus', icon: 'Target',
    desc: 'One product spotlight (e.g. Medicare only, Life only).' },
  { key: 'event', label: 'Event / Seminar', icon: 'CalendarDays',
    desc: 'Promote an event with date, place and an RSVP form.' },
  { key: 'digital_card', label: 'Digital card (link-in-bio)', icon: 'Contact',
    desc: 'Compact card with photo and quick-contact buttons.' },
  { key: 'appointments_only', label: 'Appointments only', icon: 'CalendarClock',
    desc: 'Just a "book a call" scheduling widget.' },
  { key: 'team_appointments', label: 'Team scheduler (KR)', icon: 'Users',
    desc: 'Visitor picks an agent from a roster and books.' },
  { key: 'simple', label: 'Simple referral card', icon: 'IdCard',
    desc: 'Minimal name, photo and a short contact form.' },
];

export const FIELD_VISIBILITY_OPTIONS = [
  { key: 'required', label: 'Required' },
  { key: 'optional', label: 'Optional' },
  { key: 'hidden', label: 'Hidden' },
];

export const LANDING_STATUSES = [
  { key: 'draft', label: 'Draft', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  { key: 'active', label: 'Active', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { key: 'inactive', label: 'Inactive', tone: 'bg-amber-100 text-amber-700 border-amber-300' },
];

/** Normalize an arbitrary string into a URL-safe kebab-case slug. */
export function slugify(input) {
  if (!input) return '';
  return String(input)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}
